/*
 * Portions of this code are from Novel (https://github.com/steven-tey/novel)
 * Copyright (c) 2023 Steven Tey
 * Licensed under the Apache License, Version 2.0
 */

import type { BubbleMenuPluginProps } from '@tiptap/extension-bubble-menu'
import { isNodeSelection, useCurrentEditor } from '@tiptap/react'
import { BubbleMenu } from '@tiptap/react/menus'
import type { ReactNode } from 'react'
import { forwardRef } from 'react'

export interface EditorBubbleProps
  extends Omit<BubbleMenuPluginProps, 'editor'> {
  readonly children: ReactNode
  readonly tippyOptions?: {
    placement?: string
    onHidden?: () => void
  }
}

export const EditorBubble = forwardRef<HTMLDivElement, EditorBubbleProps>(
  ({ children, tippyOptions, shouldShow, updateDelay, ...rest }, ref) => {
    const { editor: currentEditor } = useCurrentEditor()

    const defaultShouldShow: BubbleMenuPluginProps['shouldShow'] = ({
      editor,
      state,
    }) => {
      const { selection } = state
      const { empty } = selection

      // don't show bubble menu if:
      // - the editor is not editable
      // - the selected node is an image
      // - the selection is empty
      // - the selection is a node selection (for drag handles)
      if (
        !editor.isEditable ||
        editor.isActive('image') ||
        empty ||
        isNodeSelection(selection)
      ) {
        return false
      }
      return true
    }

    if (!currentEditor) return null

    return (
      <div ref={ref}>
        <BubbleMenu
          editor={currentEditor}
          shouldShow={shouldShow || defaultShouldShow}
          updateDelay={updateDelay || 250}
          options={{
            placement: tippyOptions?.placement as any,
            onHide: tippyOptions?.onHidden,
          }}
          {...rest}
        >
          {children}
        </BubbleMenu>
      </div>
    )
  },
)

EditorBubble.displayName = 'EditorBubble'

export default EditorBubble
